import axios from 'axios';

// Dynamic API URL - uses current origin (works for Railway, custom domains, and localhost)
export const getApiUrl = () => {
  // In production, API is served from the same origin as the frontend
  // In development, use REACT_APP_BACKEND_URL or fall back to current origin
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

export const API_URL = getApiUrl();
export const API = `${API_URL}/api`;

// Server error -> readable text. FastAPI `detail` can be a string, an object
// (format-lock errors) or an array (422 validation); rendering an object in a
// toast crashes the whole app (audit F-27). Returns '' when there is no
// detail so callers keep their own fallback: errorText(err) || 'Save failed'.
export const errorText = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => (x && (x.msg || x.message)) || String(x)).join('; ');
  if (d && typeof d === 'object') {
    const m = d.message || d.detail || d.error || d.reason;
    return typeof m === 'string' ? m : JSON.stringify(d);
  }
  return '';
};

// Session storage helpers for persistent login
const SESSION_KEY = 'deomart_session_token';
const USER_KEY = 'deomart_user';

// One key per payment the user is entering: the server refuses a second
// submit with the same key (double click, retry after a slow response).
export const newIdempotencyKey = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch { /* fall through */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

// Text of an error whose body arrived as a Blob (responseType: 'blob').
export const blobErrorText = async (err) => {
  try {
    const t = await (err?.response?.data?.text?.() || Promise.resolve(''));
    if (!t) return errorText(err);
    try {
      const d = JSON.parse(t)?.detail;
      if (typeof d === 'string') return d;
      if (d && typeof d === 'object') return d.message || JSON.stringify(d);
    } catch { return t.slice(0, 200); }
  } catch { /* ignore */ }
  return errorText(err);
};

export const saveSession = (token, user) => {
  try {
    localStorage.setItem(SESSION_KEY, token);
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
  } catch (e) {
    console.error('Failed to save session:', e);
  }
};

export const getStoredSession = () => {
  try {
    const token = localStorage.getItem(SESSION_KEY);
    const user = localStorage.getItem(USER_KEY);
    return {
      token,
      user: user ? JSON.parse(user) : null
    };
  } catch (e) {
    return { token: null, user: null };
  }
};

export const clearSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(USER_KEY);
  } catch (e) {
    console.error('Failed to clear session:', e);
  }
};

export const getAuthHeaders = () => {
  const { token } = getStoredSession();
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
};

// Global 401 handler: when a session expires or is revoked mid-use, every data
// call starts returning 401. Without this, the user is stranded on a page full
// of spinners and stale numbers with no idea they've been logged out. Redirect
// to /login once (auth-check calls are left to ProtectedRoute, which already
// handles them gracefully, to avoid a redirect loop).
let _handling401 = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    const isAuthCall = url.includes('/auth/');
    const onLoginPage = window.location.pathname === '/login';
    if (status === 401 && !isAuthCall && !onLoginPage && !_handling401) {
      _handling401 = true;
      clearSession();
      window.location.href = '/login?expired=1';
    }
    return Promise.reject(error);
  }
);


// Import rollback (audit F-01): the SERVER decides what a rollback means from
// the upload's own type. Ask for a preview, show it in plain words, and only
// then apply. Returns the server's result, or null if the user cancelled.
export const rollbackImportBatch = async (axios, batchId, label) => {
  const url = `${API}/import/rollback/${encodeURIComponent(batchId)}`;
  const { data: p } = await axios.delete(url, { params: { confirm: false }, withCredentials: true });
  const lines = [];
  if (p.order_lines_removed != null) lines.push(`${p.order_lines_removed} order line(s) created by this upload will be removed`);
  if (p.lines_updated_not_removed) lines.push(`${p.lines_updated_not_removed} older line(s) this upload only updated will be KEPT`);
  if (p.override_lines_restored) lines.push(`${p.override_lines_restored} line(s) this upload's override removed will come back`);
  if (p.dispatch_lines_removed != null) lines.push(`${p.dispatch_lines_removed} dispatch line(s) (${p.pieces || 0} pcs) will be removed`);
  if (p.with_goods_return) lines.push(`⚠ ${p.with_goods_return} line(s) have a goods return — rollback will be refused`);
  if (p.sales_records_removed != null) lines.push(`${p.sales_records_removed} sales-report record(s) will be removed`);
  if (p.transactions_in_snapshot != null) {
    lines.push(`The ${p.division} outstanding book goes back to its state before this upload: ${p.transactions_now} rows now → ${p.transactions_in_snapshot} rows`);
    if (p.transactions_with_payments_since_upload) lines.push(`⚠ ${p.transactions_with_payments_since_upload} row(s) have payments recorded since the upload — they will be undone`);
  }
  const msg = `Roll back "${label || p.original_name || batchId}"?\n\n- ${lines.join('\n- ')}\n\nRemoved rows are soft-deleted (recoverable). Continue?`;
  // eslint-disable-next-line no-alert
  if (!window.confirm(msg)) return null;
  const { data } = await axios.delete(url, { params: { confirm: true }, withCredentials: true });
  return data;
};
